import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, XCircle } from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { updateEmployee, getEmployeeList, getEmployeeTypeList } from "@/api/employee";
import { getSettingExcel } from "@/api/setting";
import { getShopCustomerGroup } from "@/api/shop";
import { useEmployeeStore } from "@/store/employee";
import { useShopStore } from "@/store/shop";
import { useAuthStore } from "@/store/auth";
import { toast } from 'sonner';
import { PlatformUserLinkDialog } from "./platform-user-link-dialog";
import type { Employee, EmployeeUpdate, EmployeeType, PlatformUserId } from "@/types/employee";
import type { CustomerGroup } from "@/types/shop";

interface EditEmployeeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: Employee | null;
}

export function EditEmployeeDialog({ open, onOpenChange, employee }: EditEmployeeDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isOptionLoading, setIsOptionLoading] = useState(false);
  const [baseField, setBaseField] = useState<"email" | "phoneNumber">("email");
  const [formData, setFormData] = useState<EmployeeUpdate>({
    employeeId: "",
    employeeTypeId: undefined,
    groupNo: undefined,
    name: "",
    email: "",
    phoneNumber: "",
    platformUserId: "",
    isFixedGroup: "F",
  });
  
  // 회원 연동 다이얼로그 상태
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedPlatformUser, setSelectedPlatformUser] = useState<PlatformUserId | null>(null);
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(null);
  const [employeeTypes, setEmployeeTypes] = useState<EmployeeType[]>([]);
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
  const isSubmitDisabled = isLoading || isOptionLoading;
  const customerGroupMap = useMemo(() => {
    const map = new Map<number, string>();
    customerGroups.forEach(group => {
      map.set(group.groupNo, group.groupName);
    });
    return map;
  }, [customerGroups]);
  const hasLinkedPlatformUser = useMemo(() => {
    if (!currentEmployee?.platformUserId) return false;
    return currentEmployee.platformUserId.some((platformUser) => platformUser.useLinked);
  }, [currentEmployee?.platformUserId]);

  const { selectedShop } = useShopStore();
  const { user } = useAuthStore();
  const isSuperAdmin = user?.isSuperAdmin === true;
  const allowedEmployeeTypeId = user?.allowedEmployeeTypeId ?? null;
  const allowedEmployeeGroupNo = user?.allowedEmployeeGroupNo ?? null;
  const allowedEmployeeIsFixedGroup = user?.allowedEmployeeIsFixedGroup;
  const { fetchEmployees, searchParams } = useEmployeeStore();
  
  const isCafe24User = user?.platform === 'cafe24';

  const initializeDialogData = useCallback(async (shopNo: number, employeeData: Employee) => {
    setIsOptionLoading(true);
    try {
      const [typeResponse, groupResponse, settingResponse] = await Promise.all([
        getEmployeeTypeList({ shopNo }),
        getShopCustomerGroup({ shopNo }),
        getSettingExcel({ shopNo, type: "upsert" })
      ]);

      let resolvedTypes: EmployeeType[] = [];
      if (typeResponse.code === 200 && typeResponse.data) {
        resolvedTypes = typeResponse.data.types ?? [];
        setEmployeeTypes(resolvedTypes);
      } else {
        setEmployeeTypes([]);
        if (typeResponse.message) {
          toast.error(typeResponse.message);
        } else {
          toast.error("임직원 구분 목록을 불러오는데 실패했습니다.");
        }
      }

      let resolvedGroups: CustomerGroup[] = [];
      if (groupResponse.code === 200 && groupResponse.data) {
        resolvedGroups = groupResponse.data;
        setCustomerGroups(resolvedGroups);
      } else {
        setCustomerGroups([]);
        toast.error("회원 등급 정보를 불러오는데 실패했습니다.");
      }

      let defaultIsFixedGroup: "T" | "F" = "F";
      if (settingResponse.code === 200 && settingResponse.data) {
        setBaseField(settingResponse.data.baseField);
        defaultIsFixedGroup = settingResponse.data.isFixedGroup ? "T" : "F";
      } else {
        setBaseField("email");
        if (settingResponse.message) {
          toast.error(settingResponse.message);
        }
      }

      let nextEmployeeTypeId = employeeData.employeeTypeId;
      if (!isSuperAdmin && allowedEmployeeTypeId !== null) {
        nextEmployeeTypeId = allowedEmployeeTypeId;
      } else if ((!nextEmployeeTypeId || nextEmployeeTypeId === 0) && resolvedTypes.length > 0) {
        const matchedType = resolvedTypes.find(type => type.typeName === employeeData.employeeTypeName);
        nextEmployeeTypeId = matchedType ? matchedType.typeId : resolvedTypes[0].typeId;
      }

      let nextGroupNo = employeeData.lastRequestGroupNo;
      if (!isSuperAdmin && allowedEmployeeGroupNo !== null) {
        nextGroupNo = allowedEmployeeGroupNo;
      } else if (resolvedGroups.length > 0) {
        const matchedGroup = resolvedGroups.find(group => group.groupNo === nextGroupNo);
        if (!matchedGroup) {
          nextGroupNo = resolvedGroups[0].groupNo;
        }
      }

      let nextIsFixedGroup: "T" | "F" = employeeData.isFixedGroup ?? defaultIsFixedGroup;
      if (!isSuperAdmin && typeof allowedEmployeeIsFixedGroup === "boolean") {
        nextIsFixedGroup = allowedEmployeeIsFixedGroup ? "T" : "F";
      }

      setFormData(prev => ({
        ...prev,
        employeeTypeId: nextEmployeeTypeId,
        groupNo: nextGroupNo,
        isFixedGroup: nextIsFixedGroup,
      }));
    } catch (error) {
      console.error("임직원 수정 데이터 초기화 오류:", error);
      toast.error("임직원 정보를 불러오는 중 오류가 발생했습니다.");
      setEmployeeTypes([]);
      setCustomerGroups([]);
      setBaseField("email");
    } finally {
      setIsOptionLoading(false);
    }
  }, [allowedEmployeeGroupNo, allowedEmployeeIsFixedGroup, allowedEmployeeTypeId, isSuperAdmin]);

  // 다이얼로그가 열릴 때마다 선택된 직원 정보로 폼 초기화 및 설정 정보 가져오기
  useEffect(() => {
    if (open && employee) {
      const shopNo = isCafe24User && selectedShop ? selectedShop.shopNo : 1;

      setCurrentEmployee(employee);
      setEmployeeTypes([]);
      setCustomerGroups([]);
      setBaseField("email");
      const initialEmployeeTypeId =
        !isSuperAdmin && allowedEmployeeTypeId !== null ? allowedEmployeeTypeId : employee.employeeTypeId;
      const initialGroupNo =
        !isSuperAdmin && allowedEmployeeGroupNo !== null ? allowedEmployeeGroupNo : employee.lastRequestGroupNo;
      const initialIsFixedGroup: "T" | "F" =
        !isSuperAdmin && typeof allowedEmployeeIsFixedGroup === "boolean"
          ? allowedEmployeeIsFixedGroup
            ? "T"
            : "F"
          : employee.isFixedGroup ?? "F";

      setFormData({
        employeeId: employee.employeeId,
        employeeTypeId: initialEmployeeTypeId,
        name: employee.name,
        email: employee.email,
        phoneNumber: employee.phoneNumber || "",
        platformUserId: "",
        groupNo: initialGroupNo,
        isFixedGroup: initialIsFixedGroup,
      });

      initializeDialogData(shopNo, employee);
    }
  }, [
    open,
    employee,
    isCafe24User,
    selectedShop,
    initializeDialogData,
    isSuperAdmin,
    allowedEmployeeTypeId,
    allowedEmployeeGroupNo,
    allowedEmployeeIsFixedGroup,
  ]);

  const handleInputChange = (field: keyof EmployeeUpdate, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!employee) {
      toast.error("수정할 직원 정보가 없습니다.");
      return;
    }

    // 각 항목별 상세 검증
    if (!formData.employeeId || !formData.employeeId.trim()) {
      toast.error("사원번호를 입력해주세요.");
      return;
    }
    
    if (!formData.name || !formData.name.trim()) {
      toast.error("이름을 입력해주세요.");
      return;
    }

    if (!formData.employeeTypeId) {
      toast.error("임직원 구분을 선택해주세요.");
      return;
    }
    
    if (!hasLinkedPlatformUser) {
      if (typeof formData.groupNo !== "number" || formData.groupNo <= 0) {
        toast.error("회원 등급을 선택해주세요.");
        return;
      }
  
      if (!formData.isFixedGroup) {
        toast.error("회원 등급 고정 여부를 선택해주세요.");
        return;
      }
    }
    
    // baseField에 따른 필수 입력 검증
    if (baseField === "phoneNumber") {
      // 휴대폰번호가 필수인 경우
      if (!formData.phoneNumber || !formData.phoneNumber.trim()) {
        toast.error("휴대폰번호를 입력해주세요.");
        return;
      }
      
      // 휴대폰번호 형식 검증 (한국 휴대폰번호, 숫자만)
      const phoneNumber = formData.phoneNumber.trim();
      if (phoneNumber.length < 10 || phoneNumber.length > 11) {
        toast.error("휴대폰번호는 10-11자리 숫자를 입력해주세요.");
        return;
      }
      
      // 한국 휴대폰번호 패턴 검증 (010, 011, 016, 017, 018, 019로 시작)
      if (!phoneNumber.startsWith('01') || !/^01[016789]/.test(phoneNumber)) {
        toast.error("올바른 휴대폰번호를 입력해주세요. (010, 011, 016, 017, 018, 019로 시작)");
        return;
      }
    } else {
      // 이메일이 필수인 경우 (기본값)
      if (!formData.email || !formData.email.trim()) {
        toast.error("이메일을 입력해주세요.");
        return;
      }
      
      // 이메일 형식 검증
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error("올바른 이메일 형식을 입력해주세요.");
        return;
      }
    }
    
    // 선택적 필드 검증 (필수가 아닌 경우에도 입력했다면 형식 검증)
    if (baseField === "email" && formData.phoneNumber && formData.phoneNumber.trim()) {
      // 이메일이 필수인데 휴대폰번호도 입력했다면 형식 검증
      const phoneNumber = formData.phoneNumber.trim();
      if (phoneNumber.length < 10 || phoneNumber.length > 11) {
        toast.error("휴대폰번호는 10-11자리 숫자를 입력해주세요.");
        return;
      }
      
      if (!phoneNumber.startsWith('01') || !/^01[016789]/.test(phoneNumber)) {
        toast.error("올바른 휴대폰번호를 입력해주세요. (010, 011, 016, 017, 018, 019로 시작)");
        return;
      }
    } else if (baseField === "phoneNumber" && formData.email && formData.email.trim()) {
      // 휴대폰번호가 필수인데 이메일도 입력했다면 형식 검증
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error("올바른 이메일 형식을 입력해주세요.");
        return;
      }
    }

    setIsLoading(true);
    try {
      const updateData: EmployeeUpdate = {
        employeeId: formData.employeeId.trim(),
        name: formData.name.trim(),
        employeeTypeId: formData.employeeTypeId,
      };

      // 빈 값이 아닌 경우에만 API에 포함
      if (formData.phoneNumber && formData.phoneNumber.trim()) {
        updateData.phoneNumber = formData.phoneNumber.trim();
      }
      
      if (formData.email && formData.email.trim()) {
        updateData.email = formData.email.trim().toLowerCase();
      }

      if (!hasLinkedPlatformUser && typeof formData.groupNo === "number") {
        updateData.groupNo = formData.groupNo;
      }

      if (!hasLinkedPlatformUser && formData.isFixedGroup) {
        updateData.isFixedGroup = formData.isFixedGroup;
      }
      
      // platformUserId는 배열이므로 제거
      
      const response = await updateEmployee(employee.id.toString(), updateData);
      
      if (response.code === 200) {
        // 성공 시 목록 새로고침
        await fetchEmployees({ ...searchParams });
        onOpenChange(false);
        toast.success("임직원 정보가 성공적으로 수정되었습니다.");
      } else {
        toast.error(response.message || "임직원 정보 수정에 실패했습니다.");
      }
    } catch (error) {
      console.error("임직원 정보 수정 오류:", error);
      toast.error("임직원 정보 수정 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const isReadOnlyPlatformUserId = employee?.isLinked === true;

  // 현재 직원 정보 갱신
  const refreshCurrentEmployee = async () => {
    if (!currentEmployee) return;
    
    try {
      const shopNo = isCafe24User && selectedShop ? selectedShop.shopNo : 1;
      const response = await getEmployeeList({
        shopNo,
        searchType: "employeeId",
        searchValue: currentEmployee.employeeId,
        page: 1,
        limit: 1
      });
      
      if (response.code === 200 && response.data?.employees && response.data.employees.length > 0) {
        const updatedEmployee = response.data.employees[0];
        setCurrentEmployee(updatedEmployee);
      }
    } catch (error) {
      console.error("직원 정보 갱신 오류:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>임직원 정보 수정</DialogTitle>
            <DialogDescription>
              등록된 임직원 정보를 수정할 수 있어요.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="employeeTypeId">구분</Label>
              <Select
                key={`edit-employee-type-${employeeTypes.map(type => type.typeId).join("-")}`}
                value={formData.employeeTypeId ? String(formData.employeeTypeId) : undefined}
                onValueChange={(value) => {
                  const parsed = Number(value);
                  if (Number.isNaN(parsed)) return;
                  handleInputChange("employeeTypeId", parsed);
                }}
                disabled={isOptionLoading || employeeTypes.length === 0 || !isSuperAdmin}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="구분을 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  {employeeTypes.length === 0 ? (
                    <SelectItem value="no-type" disabled>
                      등록된 구분이 없습니다.
                    </SelectItem>
                  ) : (
                    employeeTypes.map((type) => (
                      <SelectItem key={type.typeId} value={type.typeId.toString()}>
                        {type.typeName}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="employeeId">사원번호</Label>
              <Input
                id="employeeId"
                value={formData.employeeId}
                onChange={(e) => handleInputChange("employeeId", e.target.value)}
                placeholder="사원번호를 입력하세요"
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="name">이름</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="이름을 입력하세요"
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="phoneNumber">
                휴대폰번호
              </Label>
              <Input
                id="phoneNumber"
                type="tel"
                value={formData.phoneNumber || ""}
                onChange={(e) => {
                  // 숫자만 입력 허용
                  const numericValue = e.target.value.replace(/\D/g, '');
                  handleInputChange("phoneNumber", numericValue);
                }}
                placeholder={
                  baseField === "phoneNumber" 
                    ? "휴대폰번호를 입력하세요 (필수) (예: 01012345678)" 
                    : "휴대폰번호를 입력하세요 (선택) (예: 01012345678)"
                }
                disabled={isLoading}
                maxLength={11}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="email">
                이메일
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email || ""}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder={
                  baseField === "email" 
                    ? "이메일을 입력하세요 (필수)" 
                    : "이메일을 입력하세요 (선택)"
                }
                disabled={isLoading}
              />
            </div>

            {!hasLinkedPlatformUser && (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="groupNo">회원 등급</Label>
                  <Select
                    key={`edit-customer-group-${customerGroups.map(group => group.groupNo).join("-")}`}
                    value={
                      typeof formData.groupNo === "number" && formData.groupNo > 0
                        ? String(formData.groupNo)
                        : undefined
                    }
                    onValueChange={(value) => {
                      const parsed = Number(value);
                      if (Number.isNaN(parsed)) return;
                      handleInputChange("groupNo", parsed);
                    }}
                    disabled={isOptionLoading || customerGroups.length === 0 || !isSuperAdmin}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="회원 등급을 선택하세요" />
                    </SelectTrigger>
                    <SelectContent>
                      {customerGroups.length === 0 ? (
                        <SelectItem value="no-group" disabled>
                          등록된 등급이 없습니다.
                        </SelectItem>
                      ) : (
                        customerGroups.map((group) => (
                          <SelectItem key={group.groupNo} value={group.groupNo.toString()}>
                            {group.groupName}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="isFixedGroup">회원 등급 고정 여부</Label>
                  <Select
                    value={formData.isFixedGroup}
                    onValueChange={(value) => handleInputChange("isFixedGroup", value as "T" | "F")}
                    disabled={isOptionLoading || !isSuperAdmin}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="고정 여부를 선택하세요" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="T">고정</SelectItem>
                      <SelectItem value="F">미고정</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="grid gap-2">
              <Label>회원 ID</Label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {currentEmployee?.platformUserId && currentEmployee.platformUserId.length > 0 ? (
                  currentEmployee.platformUserId.map((userId) => {
                    const gradeLabel = userId.useLinked ? customerGroupMap.get(userId.groupNo) : undefined;
                    return (
                      <div key={userId.id} className="flex items-center justify-between p-2 border rounded-md">
                        <span 
                          className={`text-sm truncate flex-1 min-w-0 mr-2 ${
                            userId.useLinked ? "text-foreground" : "text-muted-foreground"
                          }`} 
                          title={
                            gradeLabel
                              ? `${userId.platformUserId} (${gradeLabel})`
                              : userId.platformUserId
                          }
                        >
                          {userId.platformUserId}
                          {userId.useLinked && gradeLabel ? (
                            <span className="text-muted-foreground"> ({gradeLabel})</span>
                          ) : null}
                        </span>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          {userId.useLinked ? (
                            <>
                              <CheckCircle2 className="h-3 w-3 text-green-600" />
                              <span className="text-xs text-green-600 font-medium">연동됨</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-3 w-3 text-gray-400" />
                              <span className="text-xs text-gray-400">비연동</span>
                            </>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedPlatformUser(userId);
                            setLinkDialogOpen(true);
                          }}
                          className="h-6 px-2 text-xs hover:bg-gray-100"
                        >
                          연동수정
                        </Button>
                      </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-sm text-muted-foreground p-2 border rounded-md bg-muted/50">
                    연동된 회원 ID가 없습니다.
                  </div>
                )}
              </div>
            </div>
            </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              취소
            </Button>
            <Button type="submit" disabled={isSubmitDisabled}>
              {isLoading ? "수정 중..." : "수정"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      
      {/* 회원 연동 다이얼로그 */}
      <PlatformUserLinkDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        platformUserId={selectedPlatformUser}
        onSuccess={async () => {
          // 성공 시 현재 직원 정보 갱신
          await refreshCurrentEmployee();
          // 직원 목록도 새로고침
          fetchEmployees({ ...searchParams });
        }}
      />
    </Dialog>
  );
}
